/*
XOL-67 (B4): how a component of the RTA screens runs an RTA command (propose, withdraw, decline, the client's
response, submit, delete, add an option ...) and says what came of it.

An RTA command is not `saveForm`: `saveForm` stamps `created_by` / `updated_by` into the body (which the v3
routes refuse or drop) and toasts in its own words. Here the request goes through PostMixins.send, QUIET (no
toast of its own, `notifyFailureCallback` on), so that every outcome comes back to the caller as a value and
the SPA owns the copy, keyed on the API's `code` (lib/rtaRefusal). One command, one answer; nothing here
rejects.

  rtaCommand(method, url, body)       -> Promise { ok, data } | { ok: false, err, failure, viewOnly, unanswered }
  rtaRefuse(result, ctx)              show the refusal (or none for a code that is silent); returns its notice
  rtaToast(sentence, data, opts)      the sentence, then what the e-mail did (the response's `notice`)
  rtaAnnounce(data)                   tell the open RTA drawer the RTA moved (the envelope's `action`)

The envelope of every RTA command is { action, option?, completion?, notice?, decisionReset?, duplicate? }
(API DEV-7). `rta-changed` is the app event the RTA drawer listens to; the layer of an option does not know
whether its parent drawer is open, so it announces and moves on.
------------------------------------------------------------------------ */

import axios from '@/lib/axiosClient'
import PostMixins from '@/mixins/PostMixins'
import { failureNotice, parseFailure } from '@/lib/rtaRefusal'
import { noticeOutcome, showToast } from '@/lib/requestActivity'

export var RTA_CHANGED = 'rta-changed'

// The last refusal toast still on screen: the same refusal shown again (a gate that re-shows a refused save, pressed twice) replaces it.
var lastNotice = null

// An option layer stacked under (or beside) the RTA drawer asks it for the RTA it holds (`rta-drawer-request` { id }); the drawer
// answers `rta-drawer` { id, action }, the SAME reactive object it shows, which carries what the option row's compact `rta`
// does not (`emails`, the counts). A layer opened cold, with no drawer, gets no answer and treats what it does not know as unknown.
export var RTA_DRAWER_REQUEST = 'rta-drawer-request'
export var RTA_DRAWER = 'rta-drawer'

export default {
  mixins: [PostMixins],

  methods: {

    /**
     * Run one command: `method` is 'post' | 'put' | 'delete', `url` the API path (getEndpoint is applied). `headers`
     * (optional, post / put): extra request headers, as `X-Edit-Session` on the decision auto-save (API DEV-17). Every call
     * carries the `?orgId=&orgCategory=` query (getEndpoint): since the API's cce0bcad a versioned FieldPatch reads the JSON body
     * only, so the query no longer turns into fields (V8c).
     * Resolves, never rejects:
     *   { ok: true, data }                       the 200 body
     *   { ok: false, err, failure }              a refusal or a network error (`failure` is parseFailure(err))
     *   { ok: false, viewOnly: true }            send() refused a view-only user before any request
     *   { ok: false, unanswered: true, silent }  send() answered with neither (a 2xx that is not a 200, or a
     *                                            non-200 `warning` / `error` body, which it has already toasted
     *                                            when `silent` is true)
     */
    rtaCommand (method, url, body, item, headers) {
      var self = this

      // API DEV-60: a POST with no JSON body is refused (500 at first; since the API's FU-11 it answers 422), so a command that takes nothing
      // still sends `{}`.
      if (method === 'post' && (body === undefined || body === null)) {
        body = {}
      }

      var verb = headers
        ? function (u, d) { return axios[method](u, d, { headers: headers }) }
        : axios[method]

      return new Promise(function (resolve) {
        var answered = false

        function done (result) {
          answered = true
          resolve(result)
        }

        Promise.resolve(
          self.send(
            self, self.getEndpoint(url), body, item || 'RTA',
            function (data) { done({ ok: true, data: data || {} }) },
            function (err) {
              if (err === undefined) {
                done({ ok: false, viewOnly: true, err: null, failure: parseFailure(null) })
              } else {
                done({ ok: false, err: err, failure: parseFailure(err) })
              }
            },
            verb, false, 'Save', null, true, false, true
          )
        ).then(function (returned) {
          if (!answered) {
            resolve({ ok: false, unanswered: true, silent: returned === null, err: null, failure: parseFailure(null) })
          }
        }, function (e) {
          if (!answered) {
            resolve({ ok: false, err: e, failure: parseFailure(e) })
          }
        })
      })
    },

    /**
     * Say why a command did not go through: one `$notify` in the words of the code (lib/rtaRefusal), the view-only
     * line, or the neutral line for an answer that was neither. Returns the notice shown (or null when none was:
     * a silent code, or one already toasted), so a caller can decide what to re-read.
     */
    rtaRefuse (result, ctx) {
      if (!result || result.ok) {
        return null
      }

      if (result.viewOnly) {
        this.$message({ message: 'You are view only, can\'t save', type: 'warning' })
        return null
      }

      if (result.unanswered) {
        if (!result.silent) {
          this.$notify({ title: 'Not saved', message: 'The server answered, but not with what was expected. Reload to see the RTA\'s current status.', type: 'warning', duration: 5500 })
        }

        return null
      }

      var notice = failureNotice(result.err, ctx)

      if (notice) {
        this.rtaNotifyOnce({ title: notice.title, message: notice.message, type: notice.type, duration: notice.duration })
      }

      return notice
    },

    /**
     $notify, but one toast per distinct message (XOL-67 B10): a refusal shown again while its toast is still up replaces it, so a gate pressed
     again and again leaves one toast, not a stack. Returns the notification.
     */
    rtaNotifyOnce (opts) {
      var key = String(opts.title) + '|' + String(opts.message)

      if (lastNotice && lastNotice.key === key && lastNotice.handle && typeof lastNotice.handle.close === 'function') {
        try {
          lastNotice.handle.close()
        } catch (e) {
          // already gone
        }
      }

      var handle = this.$notify(opts)

      lastNotice = { key: key, handle: handle }

      return handle
    },

    /**
     * The toast of a command that went through: `sentence` alone ("Option 3 declined."), or with what the e-mail did
     * after it (the envelope's `notice`, lib/requestActivity.noticeOutcome). A replay (`duplicate`) or an answer with no
     * notice (a Tower Co client, mail off) says the sentence alone.
     *
     * @param {Object} opts { actor ('towerco' | 'client'), orgs ([names the mail went to]), towerCo }
     */
    rtaToast (sentence, data, opts) {
      opts = opts || {}

      var notice = data && !data.duplicate ? data.notice : null
      var toast = notice
        ? noticeOutcome(notice, { kind: 'rta_notice', sentence: sentence, orgs: opts.orgs || [], actor: opts.actor, towerCo: opts.towerCo })
        : null

      if (toast) {
        showToast(this, toast)
      } else if (sentence) {
        this.$message({ message: sentence, type: 'success' })
      }
    },

    /**
     * Tell the RTA drawer (if one is open) that the RTA moved: the envelope's `action`, or nothing when the answer
     * had none (the drawer then reads it again).
     */
    rtaAnnounce (data, id) {
      var action = data && data.action && typeof data.action === 'object' ? data.action : null

      this.$events.emit(RTA_CHANGED, { id: action && action.id ? action.id : id, action: action })
    }

  }
}
